import { FishingReport } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useFrontload } from 'react-frontload';
import Loading from '../../components/loading/loading';
import { sortReportsByMostRelevant } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_fishing_reports/sortReportsByMostRelevant';
import { useResponsive } from '../../hooks/use_responsive';
import { useRudderstack } from '../../hooks/use_rudderstack';
import { apiV1 } from '../../lib/api';
import { WaterbodyFishingReportsPage } from './waterbody_fishing_reports_page';

interface WaterbodyFishingReportsPageContainerProps {
  urlSlug: string;
}

const WaterbodyFishingReportsPageContainer = (props: WaterbodyFishingReportsPageContainerProps) => {
  const { urlSlug } = props;
  const { isMobile } = useResponsive();
  const { anonymousId } = useRudderstack();

  const { data, frontloadMeta } = useFrontload(
    `WaterbodyFishingReportsPageContainer:${urlSlug}`,
    async () => {
      try {
        const [waterbodyRes, newestFishingReportsRes, sortedReportsRes] = await Promise.allSettled([
          apiV1.waterbodyFetch(urlSlug),
          apiV1.waterbodyFishingReportsFetch(urlSlug, {
            sort: 'newest',
            per_page: 1,
          }),
          apiV1.waterbodyFishingReportsFetch(urlSlug, {
            sort: 'most_relevant',
            per_page: 8,
          }),
        ]);

        return {
          newestFishingReport:
            newestFishingReportsRes.status === 'fulfilled' &&
            newestFishingReportsRes.value.data.data.length > 0
              ? newestFishingReportsRes.value.data.data[0]
              : null,
          sortedFishingReports:
            sortedReportsRes.status === 'fulfilled' ? sortedReportsRes.value.data.data : null,
          totalFishingReportsCount:
            sortedReportsRes.status === 'fulfilled'
              ? sortedReportsRes.value.data.metadata.count_total
              : 0,
          waterbodyDetail:
            waterbodyRes.status === 'fulfilled' ? waterbodyRes.value.data.data : null,
        };
      } catch (e) {
        console.log(e);
      }
    }
  );

  const newestFishingReport = data?.newestFishingReport || null;
  const sortedFishingReports = data?.sortedFishingReports || [];
  const waterbodyDetail = data?.waterbodyDetail;
  const totalFishingReportsCount = data?.totalFishingReportsCount;

  const [fishingReportsNearby, setFishingReportsNearby] = useState<FishingReport[]>([]);

  const fetchNearbyFishingReports = async () => {
    try {
      const res = await apiV1.forYouFishingReportsFetch({
        species: [],
        waterbodies: [waterbodyDetail.id],
        lat: waterbodyDetail.lat,
        lng: waterbodyDetail.lng,
        last_request_date: dayjs().subtract(90, 'days').format('YYYY-MM-DD'),
        anonymous_id: anonymousId,
        limit: 30,
      });
      const nearbyReports = res.data.data;
      setFishingReportsNearby(sortReportsByMostRelevant(nearbyReports));
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (frontloadMeta.done && totalFishingReportsCount === 0) {
      fetchNearbyFishingReports();
    }
  }, [totalFishingReportsCount, frontloadMeta.done]);

  const mostRecentFishingReportDate = newestFishingReport
    ? dayjs(newestFishingReport?.accepted_at).startOf('day')
    : null;

  if (frontloadMeta.pending) {
    return (
      <Loading text="Loading Waterbody Data" style={{ minHeight: isMobile ? '80dvh' : '40dvh' }} />
    );
  }

  if (!frontloadMeta.done || !waterbodyDetail) {
    return null;
  }

  return (
    <>
      <WaterbodyFishingReportsPage
        fishingReportsNearby={fishingReportsNearby}
        mostRecentFishingReportDate={mostRecentFishingReportDate}
        initialFishingReports={sortedFishingReports}
        totalFishingReportsCount={totalFishingReportsCount}
        waterbodyDetail={waterbodyDetail}
      />
    </>
  );
};
export default WaterbodyFishingReportsPageContainer;
